/**
 * Types shared by every layer: the Electron main process, the preload bridge,
 * the React renderer and the standalone web build.
 *
 * Nothing here may import from `electron` or from anything browser-only —
 * this module has to stay loadable from all four environments.
 */

// Type-only, and it has to stay that way: `sync-wire.ts` is the pinned wire
// format and erases to nothing so the server can run it through Node's type
// stripping. A value import would give this module a runtime dependency on it.
import type { ScratchpadRecord } from './sync-wire'

/** Stable note identifier. Generated with `crypto.randomUUID()`. */
export type NoteId = string

/**
 * A note as persisted by any storage backend.
 *
 * The extra bookkeeping fields (`deletedAt`, `rev`) are not used by the app
 * today. They exist so a sync engine can be layered on top of the existing
 * backends without a data migration: `deletedAt` makes deletions propagatable
 * and `rev` gives conflict resolution something to compare.
 */
export interface Note {
  id: NoteId
  /** Markdown source. The only content the app actually stores. */
  body: string
  /** Epoch milliseconds. */
  createdAt: number
  /** Epoch milliseconds. */
  updatedAt: number
  /** Tombstone. Non-null means deleted; the record is kept for sync. */
  deletedAt: number | null
  /** Monotonic per-note revision, bumped on every write. */
  rev: number
}

/**
 * Lightweight projection used by the note switcher. Never carries the full
 * body, so listing thousands of notes stays cheap.
 */
export interface NoteSummary {
  id: NoteId
  /** Derived from the first non-empty line of the body. */
  title: string
  /** Derived from the lines after the title. */
  preview: string
  createdAt: number
  updatedAt: number
}

export type ThemePreference = 'system' | 'light' | 'dark'

/**
 * How this device reaches its Jot Server.
 *
 * Inert unless `enabled` is on *and* a token has been pasted in: with either
 * missing, nothing goes on the wire at all.
 */
export interface SyncSettings {
  enabled: boolean
  /**
   * Base URL of the Jot Server; `/scratchpad` is appended to it. Stored exactly
   * as typed — deciding whether it is one this client will talk to belongs to
   * `scratchpadEndpoint`, not to a settings store.
   */
  endpoint: string
  /**
   * The Jot Server's single bearer token (ADR-0003), and the first secret either
   * settings store has held: on desktop it lands in `settings.json`, on web in
   * `localStorage`, beside everything else. Never logged, and never put in a URL.
   */
  token: string
  /**
   * What this device claims as the wire's `pushedBy`, or empty to use
   * `PlatformInfo.deviceLabel`. Client-asserted either way, which is exactly why
   * it is overridable and why it is only ever a display hint (ADR-0003).
   */
  deviceLabel: string
}

export interface Settings {
  /** Electron accelerator, e.g. `CommandOrControl+Shift+Space`. Desktop only. */
  shortcut: string
  theme: ThemePreference
  /** Editor font size in pixels. */
  fontSize: number
  /** Hide the panel as soon as it loses focus. Desktop only. */
  hideOnBlur: boolean
  /** Launch Jot at login. Desktop only. */
  launchAtLogin: boolean
  /** Show a Dock/taskbar entry. When false, Jot lives in the tray only. Desktop only. */
  showInDock: boolean
  /** Soft-wrap long lines in the editor. */
  lineWrap: boolean
  /** Render a subtle spell-check underline. */
  spellCheck: boolean
  sync: SyncSettings
}

export const DEFAULT_SHORTCUT = 'CommandOrControl+Shift+Space'

/**
 * What the OS actually gave us for the global accelerator.
 *
 * Registration fails for reasons no amount of syntax checking can predict —
 * another application already owns the combination — so the outcome has to be
 * observable by the UI rather than silently corrected. Desktop only.
 */
export interface ShortcutState {
  /** The accelerator the user asked for. Always what Settings displays. */
  accelerator: string
  /** Whether `accelerator` itself is the one now bound. */
  bound: boolean
  /**
   * What is bound in its place when `bound` is false, or null when nothing is —
   * the panel is then reachable only from the tray.
   */
  fallback: string | null
}

export const DEFAULT_SETTINGS: Settings = {
  shortcut: DEFAULT_SHORTCUT,
  theme: 'system',
  fontSize: 14,
  hideOnBlur: false,
  launchAtLogin: false,
  showInDock: false,
  lineWrap: true,
  spellCheck: false,
  sync: {
    enabled: false,
    endpoint: '',
    token: '',
    // Empty means "whatever this device calls itself", which is the answer for
    // every install that never opens this field.
    deviceLabel: '',
  },
}

/** Where the app is running and what it can do. Drives desktop-only UI. */
export interface PlatformInfo {
  kind: 'desktop' | 'web'
  /** Version from package.json — what the About panel shows. */
  appVersion: string
  electronVersion: string | null
  chromeVersion: string | null
  nodeVersion: string | null
  /** `darwin` | `win32` | `linux` on desktop, `web` in the browser. */
  os: string
  /** Human-readable description of where notes live, shown in Settings. */
  storageLabel: string
  /** Absolute path to the notes folder. Desktop only. */
  notesPath: string | null
  /**
   * What this device calls itself when it pushes: the machine's hostname on
   * desktop, `browser` in the browser. It is the default for the wire's
   * `pushedBy`, which `SyncSettings.deviceLabel` overrides.
   */
  deviceLabel: string
}

/**
 * Storage contract. `ElectronNotesStore` proxies it over IPC to markdown files
 * on disk; `WebNotesStore` implements it against IndexedDB. A future
 * `SyncedNotesStore` can wrap either one without the UI noticing.
 */
export interface NotesStore {
  list(): Promise<NoteSummary[]>
  get(id: NoteId): Promise<Note | null>
  create(body?: string): Promise<Note>
  update(id: NoteId, body: string): Promise<Note>
  remove(id: NoteId): Promise<void>
}

export interface SettingsStore {
  get(): Promise<Settings>
  set(patch: DeepPartial<Settings>): Promise<Settings>
}

/**
 * The Last-Agreed Body, as the two facts about it that are worth keeping: the
 * revision the Jot Server had assigned it, and the body itself.
 *
 * The body and not a fingerprint of it, which is what this held first. Two
 * questions are answered by comparing against it — "did I change since then" and,
 * at a `409`, "did the server" — and the second one cannot be answered
 * probabilistically: a collision would have the device conclude the server still
 * holds the agreed body and push over text it never saw, which is the silent
 * divergence ADR-0001 and ADR-0002 exist to prevent. A cryptographic hash is not
 * available to swap in either, because `crypto.subtle` needs a secure context and
 * a self-hosted web build over plain `http://` would lose sync entirely.
 *
 * So it is a second copy of the Scratchpad, deliberately. It is bounded by the
 * same cap as a Pending Conflict's body, which this record has always held, and it
 * cannot go stale: an agreement is a fact about the past, and later typing cannot
 * make it untrue that the two once held this text (see `pair`).
 *
 * That second copy is what decides where Sync State is *stored*, because the record
 * around it can hold both bodies at once — this one on every accepted push, and a
 * Pending Conflict's while one is waiting — so twice `MAX_SCRATCHPAD_BYTES`. A
 * localStorage key cannot hold that on any shipping browser, which is why the web
 * build keeps Sync State in IndexedDB beside the notes (`web-platform.ts`).
 *
 * The two are only ever written together: a body without the revision it belongs
 * to cannot be pushed against, and a revision without the body cannot answer
 * either question — a record holding one is read as no agreement at all
 * (`asLastAgreed`). That is what `SyncStateStore` means by `Partial` rather than
 * `DeepPartial`.
 */
export interface LastAgreed {
  /** Server-assigned, so at least 1 — `0` is how the wire says "nothing pushed yet". */
  rev: number
  body: string
}

/**
 * Sync State: per-device bookkeeping about which Note is the Scratchpad, the
 * Last-Agreed Body this device and the Jot Server last held in common, and a
 * Conflict waiting for the user.
 *
 * It belongs to one device rather than to a Note, which is why it is stored
 * outside the notes folder — that folder is meant to be copied, synced by other
 * tools and opened in other editors, and carrying an agreement this device never
 * made would be worse than carrying nothing.
 *
 * `scratchpadId` is null until this device pairs. Sync is off by default, so
 * that is the ordinary state and not a degraded one: ADR-0001 keeps recency as
 * the bootstrap path for it.
 */
export interface SyncState {
  scratchpadId: NoteId | null
  /** Null until this device and the Jot Server have agreed on a body once. */
  lastAgreed: LastAgreed | null
  /**
   * The Pending Conflict, held as the record that won — in full, because the
   * `409` (or the `GET` that discovered it) already carried it and ADR-0003
   * exists partly so no second round trip is needed to describe a Conflict.
   *
   * Non-null means sync has stopped pushing until the user resolves it, and
   * nothing else: editing continues exactly as before (ADR-0002).
   */
  pendingConflict: ScratchpadRecord | null
}

/**
 * Sync State persistence. `<userData>/sync-state.json` on desktop, reached over
 * IPC; an IndexedDB record in the web build, because the two bodies above do not
 * fit a localStorage key.
 *
 * One contract on both platforms, and where it lives is the only difference: `set`
 * validates the patch through `assertSyncStatePatch` before touching storage and
 * throws rather than swallowing a write that did not land — alone among Jot's
 * stores, because the sync engine reports an unrecorded agreement instead of
 * assuming one — while `get` answers with the unpaired value for anything it cannot
 * read back, which is what ADR-0001's recency bootstrap exists to absorb.
 *
 * `Partial` and not `DeepPartial` on purpose: the Last-Agreed Body arrives as a
 * body and the revision it belongs to, together or not at all, so patching one
 * half of such a record is not an operation worth expressing.
 */
export interface SyncStateStore {
  get(): Promise<SyncState>
  set(patch: Partial<SyncState>): Promise<SyncState>
}

/** Window actions. No-ops in the web build. */
export interface WindowControls {
  hide(): void
  quit(): void
  openNotesFolder(): void
}

/**
 * The single object the React tree consumes. `createPlatform()` returns the
 * Electron-backed implementation when `window.jot` is present and the
 * IndexedDB one otherwise, so no component ever branches on the environment.
 */
export interface Platform {
  info: PlatformInfo
  notes: NotesStore
  settings: SettingsStore
  syncState: SyncStateStore
  window: WindowControls
  /** Subscribe to settings changed elsewhere (tray menu, another window). Returns an unsubscribe fn. */
  onSettingsChanged(listener: (settings: Settings) => void): () => void
  /** Subscribe to notes changed outside this renderer (file watcher, another window). */
  onNotesChanged(listener: () => void): () => void
}

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K]
}
